import { useCallback, useEffect, useRef, useState } from 'react';
import { Brain, ChevronDown, ChevronUp, Pencil, Trash2, Zap } from 'lucide-react';
import { toSectionKey } from '../hooks/useJourneyAccordionParams';
import { journeyService } from '../services/journeyService';
import type {
    JourneyChapterTemplateFirestore,
    JourneyItemTemplateFirestore,
    JourneySectionTemplateFirestore,
} from '../services/journeyTypes';
import { EditChapterModal } from './EditChapterModal';
import { SectionAccordion } from './SectionAccordion';

interface ChapterSectionProps {
    chapter: JourneyChapterTemplateFirestore;
    isOpen: boolean;
    onToggle: () => void;
    openSectionIds: Set<string>;
    onToggleSection: (chapterId: string, sectionId: string) => void;
    onSectionsLoaded: (chapterId: string, sectionIds: string[]) => void;
    onChapterUpdated: () => void;
}

interface SectionWithItems {
    section: JourneySectionTemplateFirestore;
    items: JourneyItemTemplateFirestore[];
}

const CHAPTER_BG_CLASS: Record<string, string> = {
    yellow: 'bg-yellow-200 hover:bg-yellow-300',
    blue: 'bg-blue-200 hover:bg-blue-300',
    green: 'bg-green-200 hover:bg-green-300',
    pink: 'bg-pink-200 hover:bg-pink-300',
};

export const ChapterSection = ({
    chapter,
    isOpen,
    onToggle,
    openSectionIds,
    onToggleSection,
    onSectionsLoaded,
    onChapterUpdated,
}: ChapterSectionProps) => {
    const [sectionsWithItems, setSectionsWithItems] = useState<SectionWithItems[]>([]);
    const [isInitialLoading, setIsInitialLoading] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [isEditingChapter, setIsEditingChapter] = useState(false);
    const hasLoadedOnceRef = useRef(false);
    const onSectionsLoadedRef = useRef(onSectionsLoaded);
    onSectionsLoadedRef.current = onSectionsLoaded;

    const fetchSectionsAndItems = useCallback(async () => {
        if (!chapter.id) return;

        if (hasLoadedOnceRef.current) {
            setIsRefreshing(true);
        } else {
            setIsInitialLoading(true);
        }

        const sections = await journeyService.getSections(chapter.id);

        const results = await Promise.all(
            sections.map(async (section) => {
                const items = await journeyService.getItems(chapter.id!, section.id!);
                return { section, items };
            }),
        );

        setSectionsWithItems(results);
        hasLoadedOnceRef.current = true;
        setIsInitialLoading(false);
        setIsRefreshing(false);

        onSectionsLoadedRef.current(
            chapter.id,
            results.map(({ section }) => section.id!).filter(Boolean),
        );
    }, [chapter.id]);

    useEffect(() => {
        if (!isOpen || !chapter.id) {
            hasLoadedOnceRef.current = false;
            setSectionsWithItems([]);
            setIsInitialLoading(false);
            setIsRefreshing(false);
            return;
        }

        fetchSectionsAndItems();
    }, [isOpen, chapter.id, refreshTrigger, fetchSectionsAndItems]);

    const handleItemUpdated = () => {
        setRefreshTrigger((prev) => prev + 1);
    };

    const handleChapterSave = async (updated: JourneyChapterTemplateFirestore) => {
        await journeyService.updateChapter(updated);
        setIsEditingChapter(false);
        onChapterUpdated();
    };

    const handleChapterDelete = async () => {
        if (!chapter.id) return;
        const confirmed = window.confirm(
            `Delete Chapter ${chapter.number}: "${chapter.title || 'Untitled'}"? This permanently deletes the chapter and ALL its sections and items. This cannot be undone.`,
        );
        if (!confirmed) return;
        await journeyService.deleteChapter(chapter.id);
        onChapterUpdated();
    };

    const totalItems = sectionsWithItems.reduce((sum, s) => sum + s.items.length, 0);
    const totalBrainPoints = sectionsWithItems.reduce((sum, s) => sum + s.items.reduce((iSum, item) => iSum + item.brainPoints, 0), 0);
    const totalEnergyCost = sectionsWithItems.reduce((sum, s) => sum + s.items.reduce((iSum, item) => iSum + item.energyCost, 0), 0);

    const bgClass = CHAPTER_BG_CLASS[chapter.color] ?? 'bg-blue-200 hover:bg-blue-300';
    const hasSections = sectionsWithItems.length > 0;

    return (
        <div>
            <div
                className={`rounded-lg py-3 px-4 cursor-pointer transition-colors ${bgClass}`}
                onClick={onToggle}
            >
                {/* Row 1: Title + Edit + Chevron */}
                <div className="flex justify-between items-center gap-2">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 min-w-0">
                        <h2 className="text-lg font-bold text-gray-900 break-words">
                            Chapter {chapter.number}: {chapter.title || 'Untitled'}
                        </h2>
                        {chapter.id && (
                            <span className="text-xs font-mono text-gray-400 break-all">id: {chapter.id}</span>
                        )}
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsEditingChapter(true);
                            }}
                            className="p-1 rounded hover:bg-white/40 transition-colors text-gray-600 hover:text-gray-900"
                            title="Edit chapter"
                        >
                            <Pencil size={16} />
                        </button>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                handleChapterDelete();
                            }}
                            className="p-1 rounded hover:bg-white/40 transition-colors text-gray-600 hover:text-red-600"
                            title="Delete chapter"
                        >
                            <Trash2 size={16} />
                        </button>
                    </div>
                    {isOpen ? <ChevronUp size={20} className="shrink-0" /> : <ChevronDown size={20} className="shrink-0" />}
                </div>

                {/* Row 2: Badges */}
                {hasSections && (
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500 text-white">
                            {sectionsWithItems.length} sections, {totalItems} items
                        </span>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-pink-100 text-pink-800">
                            <Brain className="w-3 h-3" />
                            {totalBrainPoints}
                        </span>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            <Zap className="w-3 h-3" />
                            {totalEnergyCost}
                        </span>
                    </div>
                )}
            </div>

            {isOpen && (
                <div className="relative mt-2 space-y-4">
                    {isRefreshing && (
                        <div className="absolute inset-0 z-10 flex items-start justify-center bg-white/60 pt-8">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
                        </div>
                    )}

                    {isInitialLoading && (
                        <div className="flex items-center justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                        </div>
                    )}

                    {!isInitialLoading && !hasSections && (
                        <p className="text-gray-500 text-sm py-4 text-center">No sections found in this chapter.</p>
                    )}

                    {hasSections &&
                        sectionsWithItems.map(({ section, items }) => (
                            <div key={section.id} className="ml-2 sm:ml-4">
                                <SectionAccordion
                                    section={section}
                                    items={items}
                                    chapterId={chapter.id!}
                                    isOpen={openSectionIds.has(toSectionKey(chapter.id!, section.id!))}
                                    onToggle={() => onToggleSection(chapter.id!, section.id!)}
                                    onItemUpdated={handleItemUpdated}
                                />
                            </div>
                        ))}
                </div>
            )}

            {isEditingChapter && (
                <EditChapterModal
                    chapter={chapter}
                    onSave={handleChapterSave}
                    onCancel={() => setIsEditingChapter(false)}
                />
            )}
        </div>
    );
};
